/**
 * Annuaire "villes desservies" pour les hubs /chauffeur et /location.
 *
 * Dérivé des REGISTRES (chauffeurPages + slugs location) → auto-à-jour quand on
 * ajoute une ville. Fournit des liens internes forts depuis les pages hub (forte
 * autorité) vers les 48 pages ville chauffeur + 15 pages ville location, en
 * FR/EN/AR. Noms de villes en graphie latine accentuée (noms propres), heading
 * localisé côté composant.
 */
import { chauffeurPages } from "@/(global)/chauffeur/slug-chauffeur";
import { frToEnChauffeurSlug } from "@/(global)/chauffeur/chauffeur-en";
import type { Locale } from "@/config/i18n";

import {
  RENTAL_CITY_SLUGS,
  chauffeurHref as chauffeurHrefBase,
  cityName,
  clusterChauffeur,
  clusterLocation,
} from "./maillage.mjs";

export type CityLink = { href: string; label: string };

/** Slugs chauffeur qui sont des VILLES/localités (pas événement/véhicule/hub). */
const CHAUFFEUR_CITY_SLUGS = new Set<string>([
  "cannes",
  "nice",
  "monaco",
  "antibes",
  "saint-tropez",
  "cote-d-azur",
  "saint-jean-cap-ferrat",
  "eze",
  "villefranche-sur-mer",
  "mougins",
  "sophia-antipolis",
  "mandelieu-la-napoule",
  "le-cannet",
  "vallauris-golfe-juan",
  "juan-les-pins",
  "theoule-sur-mer",
  "grasse",
  "valbonne",
  "biot",
  "villeneuve-loubet",
  "cagnes-sur-mer",
  "saint-laurent-du-var",
  "saint-paul-de-vence",
  "vence",
  "beaulieu-sur-mer",
  "cap-d-ail",
  "beausoleil",
  "menton",
  "roquebrune-cap-martin",
  "la-turbie",
  "tourrettes-sur-loup",
  "la-colle-sur-loup",
  "mouans-sartoux",
  "opio",
  "roquefort-les-pins",
  "saint-raphael",
  "frejus",
  "sainte-maxime",
  "grimaud",
  "port-grimaud",
  "cogolin",
  "gassin",
  "la-croix-valmer",
  "ramatuelle",
  "cavalaire-sur-mer",
  "le-lavandou",
  "bormes-les-mimosas",
  "hyeres",
  "toulon",
]);

/** Liens ville CHAUFFEUR, triés alpha, localisés (FR/EN/AR). */
export const chauffeurCityLinks = (locale: Locale): CityLink[] =>
  chauffeurPages
    .filter((p) => CHAUFFEUR_CITY_SLUGS.has(p.slug))
    .map((p) => {
      const enSlug = frToEnChauffeurSlug(p.slug) ?? p.slug;
      const href =
        locale === "en"
          ? `/en/chauffeur/${enSlug}`
          : locale === "ar"
            ? `/ar/chauffeur/${p.slug}`
            : `/chauffeur/${p.slug}`;
      return { href, label: cityName(p.slug, locale) };
    })
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));

/** Liens ville LOCATION, triés alpha, localisés (FR/EN/AR). */
export const rentalCityLinks = (locale: Locale): CityLink[] =>
  RENTAL_CITY_SLUGS.map((slug) => {
    const href =
      locale === "en"
        ? `/en/location/car-rental-${slug}`
        : locale === "ar"
          ? `/ar/location/location-voiture-${slug}`
          : `/location/location-voiture-${slug}`;
    return { href, label: cityName(slug, locale) };
  }).sort((a, b) => a.label.localeCompare(b.label, "fr"));

// ---------------------------------------------------------------------------
// Maillage SÉMANTIQUE : liens "jumeau" (chauffeur↔location de la même ville) +
// "villes proches" (cluster géographique). Renforce le maillage interne bien
// au-delà des annuaires (footer/hub) : liens contextuels à ancre riche.
// ---------------------------------------------------------------------------

const chauffeurHref = (slug: string, locale: Locale): string =>
  chauffeurHrefBase(slug, locale, frToEnChauffeurSlug);

/**
 * ⛔⛔ POURQUOI IL Y A PLUSIEURS FORMULATIONS ET PAS UNE SEULE.
 *
 * Mesure du coffre, etude sur 23 millions de liens internes : le facteur qui
 * correle LE PLUS avec le trafic d'une page n'est pas le nombre de liens
 * internes qu'elle recoit, c'est le nombre de FORMULATIONS D'ANCRES distinctes.
 * L'erreur nommee n°1 de cette etude est « l'ancre unique repetee partout ».
 *
 * Avant le 21/09/2026, ce fichier produisait exactement ca : un seul gabarit,
 * « Chauffeur privé à {ville} », pour la totalite du maillage de cluster du
 * site. Mesure du jour : /en/location/car-rental-cannes recevait 56 liens
 * entrants pour 2 formulations, soit 96 % de ses liens portes par une seule
 * ancre. /chauffeur/cannes : 79 %.
 *
 * ⛔ Le choix DOIT rester deterministe. Un tirage aleatoire changerait les
 * ancres a chaque compilation, donc le HTML de 730 pages a chaque deploiement,
 * pour rien. La graine combine page source, page cible et langue : deux pages
 * differentes citent la meme cible autrement, et la meme page dit toujours la
 * meme chose.
 */

/**
 * ⛔ Les gabarits eux-memes vivent dans `maillage.mjs`, pas ici.
 * Ce fichier genere les liens ; `scripts/audit-maillage.mjs` les compte et
 * bloque le build. Les deux doivent lire les MEMES formulations, sinon la
 * mesure devient fausse le jour ou quelqu'un en ajoute une.
 */

/** Page CHAUFFEUR ville → jumeau location (si dispo) + 4 villes proches. */
export const chauffeurClusterLinks = (
  slug: string,
  locale: Locale,
): CityLink[] => clusterChauffeur(slug, locale, frToEnChauffeurSlug);

// ---------------------------------------------------------------------------
// Agenda ÉVÉNEMENTS Côte d'Azur : slugs chauffeur "événement" → annuaire browsable
// (hub /chauffeur + page /evenements). Capte "événements côte d'azur / <event>".
// ---------------------------------------------------------------------------

/** Slugs chauffeur qui sont des ÉVÉNEMENTS (pas ville/véhicule). */
const EVENT_SLUGS: string[] = [
  "festival-de-cannes",
  "cannes-lions",
  "mipim",
  "mapic-cannes",
  "canneseries",
  "iltm-cannes",
  "tfwa-cannes",
  "cannes-yachting-festival",
  "regates-royales-cannes",
  "grand-prix-de-monaco",
  "monaco-e-prix",
  "monaco-yacht-show",
  "monte-carlo-masters",
  "jumping-international-monaco",
  "carnaval-de-nice",
  "nice-jazz-festival",
  "ironman-france-nice",
  "jazz-a-juan",
  "les-voiles-de-saint-tropez",
  "giraglia-saint-tropez",
  "fete-du-citron-menton",
  "fete-du-jasmin-grasse",
];

const EVENT_NAME: Record<string, string> = {
  "festival-de-cannes": "Festival de Cannes",
  "cannes-lions": "Cannes Lions",
  mipim: "MIPIM",
  "mapic-cannes": "MAPIC Cannes",
  canneseries: "CANNESERIES",
  "iltm-cannes": "ILTM Cannes",
  "tfwa-cannes": "TFWA Cannes",
  "cannes-yachting-festival": "Cannes Yachting Festival",
  "regates-royales-cannes": "Régates Royales de Cannes",
  "grand-prix-de-monaco": "Grand Prix de Monaco",
  "monaco-e-prix": "Monaco E-Prix",
  "monaco-yacht-show": "Monaco Yacht Show",
  "monte-carlo-masters": "Rolex Monte-Carlo Masters",
  "jumping-international-monaco": "Jumping International de Monaco",
  "carnaval-de-nice": "Carnaval de Nice",
  "nice-jazz-festival": "Nice Jazz Festival",
  "ironman-france-nice": "Ironman France Nice",
  "jazz-a-juan": "Jazz à Juan",
  "les-voiles-de-saint-tropez": "Les Voiles de Saint-Tropez",
  "giraglia-saint-tropez": "Giraglia Saint-Tropez",
  "fete-du-citron-menton": "Fête du Citron — Menton",
  "fete-du-jasmin-grasse": "Fête du Jasmin — Grasse",
};

/**
 * Periode de chaque evenement, pour que le lien dise QUAND, pas seulement quoi.
 *
 * SOURCE : la phrase de situation ecrite sur la page de l'evenement lui-meme
 * (« en mars a Cannes », « fin septembre », « mi-avril »…). Rien n'est ajoute
 * de memoire : 21 des 22 pages datent leur evenement, et `ironman-france-nice`
 * ne le fait pas — il n'a donc PAS de periode ici, et son lien s'affiche sans.
 * Une cle absente est le cas normal, pas une erreur.
 */
const MONTH: Record<string, Record<Locale, string>> = {
  fevrier: { fr: "février", en: "February", ar: "فبراير" },
  mars: { fr: "mars", en: "March", ar: "مارس" },
  avril: { fr: "avril", en: "April", ar: "أبريل" },
  mai: { fr: "mai", en: "May", ar: "مايو" },
  juin: { fr: "juin", en: "June", ar: "يونيو" },
  juillet: { fr: "juillet", en: "July", ar: "يوليو" },
  aout: { fr: "août", en: "August", ar: "أغسطس" },
  septembre: { fr: "septembre", en: "September", ar: "سبتمبر" },
  octobre: { fr: "octobre", en: "October", ar: "أكتوبر" },
  novembre: { fr: "novembre", en: "November", ar: "نوفمبر" },
  decembre: { fr: "décembre", en: "December", ar: "ديسمبر" },
};

/** Cle = slug evenement. Valeur = un mois, ou deux quand l'evenement est a cheval. */
const EVENT_MONTHS: Record<string, string[]> = {
  "festival-de-cannes": ["mai"],
  "cannes-lions": ["juin"],
  mipim: ["mars"],
  "mapic-cannes": ["novembre"],
  canneseries: ["avril"],
  "iltm-cannes": ["decembre"],
  "tfwa-cannes": ["octobre"],
  "cannes-yachting-festival": ["septembre"],
  "regates-royales-cannes": ["septembre"],
  "grand-prix-de-monaco": ["mai"],
  "monaco-e-prix": ["mai"],
  "monaco-yacht-show": ["septembre"],
  "monte-carlo-masters": ["avril"],
  "jumping-international-monaco": ["juin"],
  "carnaval-de-nice": ["fevrier"],
  "nice-jazz-festival": ["juillet"],
  "jazz-a-juan": ["juillet"],
  "les-voiles-de-saint-tropez": ["septembre", "octobre"],
  "giraglia-saint-tropez": ["juin"],
  "fete-du-citron-menton": ["fevrier"],
  "fete-du-jasmin-grasse": ["aout"],
};

/** « MIPIM — mars », « Les Voiles de Saint-Tropez — septembre-octobre ». */
export const eventPeriod = (slug: string, locale: Locale): string | undefined =>
  EVENT_MONTHS[slug]?.map((m) => MONTH[m][locale]).join("-");

/** Liens événement, ordre éditorial ci-dessus, localisés (FR/EN/AR). */
export const eventLinks = (locale: Locale): CityLink[] =>
  EVENT_SLUGS.map((slug) => {
    const name = EVENT_NAME[slug] ?? slug;
    const period = eventPeriod(slug, locale);
    return {
      href: chauffeurHref(slug, locale),
      label: period ? `${name} — ${period}` : name,
    };
  });

/** Page LOCATION ville → jumeau chauffeur + 4 villes de location proches. */
export const rentalClusterLinks = (slug: string, locale: Locale): CityLink[] =>
  clusterLocation(slug, locale, frToEnChauffeurSlug);
